import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSearchParams } from 'next/navigation';
import { StatefulButton } from '@openedx/paragon';
import { Form } from 'components/ui/atoms';
import { CheckoutTestResult } from 'components/ui/organisms/monarch/rules/checkout/types';
import { elementEvents, ElementType } from 'helpers/eventing';
import { MonarchDataConsumer } from 'helpers/eventing/monarch/MonarchDataConsumer';
import { centsToDollarString } from 'helpers/priceHelpers';
import {
  getAccountId,
  getIsCTADisabled,
  setIsCTADisabled,
  useAppDispatch,
  useAppSelector,
} from 'store';
import { useClearCart } from 'frontastic/hooks';
import { couponSchema } from './coupon-schema';
import { CouponFormProps, CouponSchema } from './types';
import './order-summary.scss';

export const CouponForm = ({
  cartDiscount,
  hasDiscount,
  onApplyCoupon,
  onRemoveCoupon,
}: CouponFormProps) => {
  const dispatch = useAppDispatch();
  const { clearCart } = useClearCart();
  const searchParams = useSearchParams();
  const isCTADisabled = useAppSelector(getIsCTADisabled);
  const [isCTAPending, setIsCTAPending] = useState(false);
  const customerId = useAppSelector(getAccountId);

  const buttonState = isCTAPending
    ? 'pending'
    : isCTADisabled
      ? 'disabled'
      : 'default';

  const setCTAState = (value: boolean) => {
    setIsCTAPending(value);
    dispatch(setIsCTADisabled(value));
  };

  const applyCouponTagularElement = {
    elementType: ElementType.Button,
    name: 'apply-discount-cta',
    text: 'Apply',
    htmlId: 'apply-discount-click',
    location: 'checkout',
  };

  const { elementClicked } = elementEvents({
    webElement: applyCouponTagularElement,
  });

  const handleApplyCoupon = async ({ coupon }: CouponSchema) => {
    if (coupon) {
      elementClicked();
      setCTAState(true);

      const code = coupon.toLocaleUpperCase();
      const skus = searchParams.getAll('sku') ?? [];
      const programId = searchParams.get('bundle');
      const shouldRedirectToLegacyEcommerce = await onApplyCoupon(code, skus);

      if (shouldRedirectToLegacyEcommerce) {
        // Redirect to legacy e-commerce checkout page if discount is not found in CT.
        console.info(
          `Coupon not found in CT, redirecting to legacy e-commerce for coupon: ${code} and customer: ${customerId}`,
        );
        await clearCart();

        let redirectURL = process.env.NEXT_PUBLIC_EDX_LEGACY_ECOM_BASE_URL!;
        const params = new URLSearchParams();
        params.set('code', coupon);

        if (programId) {
          params.set('bundle', programId);
          redirectURL += process.env.NEXT_PUBLIC_EDX_ECOMMERCE_BASKET_ADD_PATH!;
        } else {
          redirectURL +=
            process.env.NEXT_PUBLIC_EDX_ECOMMERCE_COUPON_REDEEM_PATH!;
        }

        skus.forEach((sku) => params.append('sku', sku));
        window.location.replace(`${redirectURL}?${params.toString()}`);
      } else {
        setCTAState(false);
      }
    }
  };

  const handleRemoveCoupon = async () => {
    if (cartDiscount?.discount) {
      setCTAState(true);
      await onRemoveCoupon(cartDiscount.discount);
      setCTAState(false);
    }
  };

  if (hasDiscount) {
    return (
      <div aria-label="applied-coupon" className="d-flex align-items-center">
        <span className="text-muted" style={{ fontSize: '0.9rem' }}>
          {cartDiscount.discount?.type === 'absolute' ? (
            <FormattedMessage
              id="payment.coupon.benefit.absolute"
              defaultMessage="Coupon {code} applied for {discountAmount} off"
              description="A coupon has been applied for a absolute coupon."
              values={{
                code: cartDiscount?.discount?.code,
                discountAmount: centsToDollarString(
                  cartDiscount.discount?.value?.[0].centAmount,
                  cartDiscount.discount?.value?.[0].fractionDigits,
                ),
              }}
            />
          ) : (
            <FormattedMessage
              id="payment.coupon.benefit.relative"
              defaultMessage="Coupon {code} applied for {discountAmount}% off"
              description="A coupon has been applied for a percentage coupon, like 10%.  Please place the % symbol as appropriate."
              values={{
                code: cartDiscount?.discount?.code,
                discountAmount: cartDiscount?.percent,
              }}
            />
          )}
        </span>
        <StatefulButton
          id="removeCouponButton"
          className="display-inline p-0 pl-3 border-0 remove-coupon-btn-link"
          style={{ fontSize: '0.9rem' }}
          variant="link"
          onClick={handleRemoveCoupon}
          state={buttonState}
          disabledStates={['pending', 'disabled']}
          icons={{
            pending: undefined,
          }}
          labels={{
            default: (
              <FormattedMessage
                id="payment.coupon.remove.form.submit.button"
                defaultMessage="Remove"
                description="Button for submitting the remove coupon form"
              />
            ),
          }}
        />
      </div>
    );
  }

  return (
    <Form
      aria-label="coupon-form"
      className="d-flex flex-column"
      validationSchema={couponSchema}
      defaultValues={{ coupon: '' }}
      shouldResetForm
      onSubmit={handleApplyCoupon}
    >
      <Form.Label htmlFor="coupon" className="h6 apply-coupon-label">
        <FormattedMessage
          id="payment.coupon.label"
          defaultMessage="Add coupon code (optional)"
          description="Label for the add coupon form"
        />
      </Form.Label>
      <div className="coupon-row">
        <div className="col-lg-9 p-0">
          <Form.Input
            name="coupon"
            label=""
            autocomplete="off"
            styleClasses="mb-0 mr-2"
            type="text"
            disabled={isCTADisabled}
          />
        </div>
        <div className="col-lg-3 p-0 text-lg-right">
          <MonarchDataConsumer<CheckoutTestResult> experimentKey="checkout">
            {(data) => (
              <StatefulButton
                id="applyCouponButton"
                variant="outline-primary"
                type="submit"
                state={buttonState}
                disabledStates={['pending', 'disabled']}
                style={{
                  background: '#00262B',
                  borderRadius: 100,
                  color: '#fff',
                  alignItems: 'center',
                }}
                labels={{
                  default: (
                    <FormattedMessage
                      id="payment.coupon.form.submit.button"
                      defaultMessage={data?.data?.promoCodeCtaText ?? 'Apply'}
                      description="Button for submitting the add coupon form"
                    />
                  ),
                  pending: (
                    <FormattedMessage
                      id="payment.coupon.form.submit.button.loading"
                      defaultMessage="Applying"
                      description="Button for submitting the add coupon form when it is in the pending state"
                    />
                  ),
                }}
              />
            )}
          </MonarchDataConsumer>
        </div>
      </div>
    </Form>
  );
};
